import { useUser } from "contexts/UserContext";
import i18n from "i18next";
import moment, { MomentInput } from "moment-timezone";
import { useMemo } from "react";
import { getSessionUser } from "services/users/auth";

/**
 * Two kinds of entry because moment has no localized token for a month and day without a
 * year, and a hand-written pattern would pin the app to English word order. The examples are
 * the English rendering; every entry follows the UI language.
 */
const DateFormat = {
  compactDate: "ll", // Mar 5, 2026
  fullDate: "LL", // March 5, 2026
  monthDay: { month: "short", day: "numeric" }, // Mar 5
  weekdayDate: { weekday: "short", month: "short", day: "numeric" }, // Thu, Mar 5
  weekday: "dddd", // Thursday
  weekdayTime: "dddd · LT", // Thursday · 2:30 PM

  sessionDateTime: {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }, // Thursday, Mar 5, 2:30 PM
  compactDateTime: "lll", // Mar 5, 2026 2:30 PM
  compactDateTimeWithOffset: "lll Z", // Mar 5, 2026 2:30 PM +01:00

  fullTime: "LT", // 2:30 PM
} as const satisfies Record<string, string | Intl.DateTimeFormatOptions>;

export type DateFormatKey = keyof typeof DateFormat;

export function useUserTimezone() {
  const timezoneId = useUser().profile.timezone.id;

  return useMemo(
    () => ({
      timezoneId,
      moment: timezoneMoment(timezoneId),
      format: (date: MomentInput, formatStr: DateFormatKey) =>
        formatDate(date, timezoneId, formatStr),
    }),
    [timezoneId]
  );
}

export const timezoneMoment =
  (tz: string) =>
  (time?: MomentInput): moment.Moment =>
    time === undefined ? moment.tz(tz) : moment.tz(time, tz);

export function localMoment(input?: MomentInput): moment.Moment {
  const zone = moment.tz.guess();
  return input === undefined ? moment.tz(zone) : moment.tz(input, zone);
}

export function formatInUserTimezone(date: MomentInput, formatStr: DateFormatKey) {
  return formatDate(date, getUserTimezoneId(), formatStr);
}

export function formatDate(
  date: MomentInput,
  timezoneId: string,
  formatStr: DateFormatKey
) {
  const spec = DateFormat[formatStr];
  // Parse in the target zone either way, so a zone-less input means the same wall clock
  // whichever branch renders it.
  const zoned = moment.tz(date, timezoneId);

  if (typeof spec === "string") {
    return zoned.format(spec);
  }

  return new Intl.DateTimeFormat(i18n.language, {
    timeZone: timezoneId,
    ...spec,
  }).format(zoned.valueOf());
}

/**
 * `formatRange` rather than two formats joined: Intl drops the repeated month and picks the
 * separator per locale ("Mar 3 – 9", "3.–9. März").
 *
 * Omit `timezoneId` only for a react-big-calendar fake date, whose device-zone wall clock is
 * already the one to show (see toFakeDate); everything else names the zone.
 */
export function formatMonthDayRange(
  from: MomentInput,
  to: MomentInput,
  timezoneId?: string
) {
  // The zone only matters for a zone-less string; an instant's value is the same in any zone.
  const instant = (value: MomentInput) =>
    (timezoneId ? moment.tz(value, timezoneId) : moment.utc(value)).valueOf();

  return new Intl.DateTimeFormat(i18n.language, {
    ...(timezoneId ? { timeZone: timezoneId } : {}),
    ...DateFormat.monthDay,
  }).formatRange(instant(from), instant(to));
}

export function getUserTimezoneId() {
  const user = getSessionUser(true);
  return user.profile.timezone?.id ?? moment.tz.guess();
}

export function getLocalTimezoneDisplay() {
  return getTimezoneDisplay(moment.tz.guess());
}

export function getUserTimezoneDisplay() {
  return getTimezoneDisplay(getUserTimezoneId());
}

export function getUTCOffset(timezoneId: string) {
  return moment.tz(timezoneId).format("Z");
}

export function getTimezoneDisplay(timezoneId: string) {
  const utcOffset = moment.tz(timezoneId).format("Z");
  return `${utcOffset} ${timezoneId}`.replace(TIMEZONE_REGION_PREFIX, " ");
}

const TIMEZONE_REGION_PREFIX = /( .+\/)/gi;

export function getTimezones(): string[] {
  return moment.tz
    .names()
    .filter(timezoneId => timezoneId.includes("/") && !timezoneId.startsWith("Etc/"))
    .map(getTimezoneDisplay)
    .sort(compareTimezones);
}

export function getTimezoneIdByPartialName() {
  const name = getLocalTimezoneDisplay().split(" ")[1] ?? "";
  return moment.tz.names().find(tzName => tzName.includes(name));
}

function compareTimezones(a: string, b: string) {
  const offsetDifference = getTimezoneOffsetInMinutes(a) - getTimezoneOffsetInMinutes(b);
  if (offsetDifference !== 0) {
    return offsetDifference;
  }

  return a.slice(7).localeCompare(b.slice(7));

  function getTimezoneOffsetInMinutes(timezone: string) {
    const sign = timezone[0] === "-" ? -1 : 1;
    const hours = Number.parseInt(timezone.slice(1, 3), 10);
    const minutes = Number.parseInt(timezone.slice(4, 6), 10);
    return sign * (hours * 60 + minutes);
  }
}

export function compareWithLocalTimezone(timezoneId: string) {
  const localTimezoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const areTimezonesEquivalent =
    moment.tz.zone(localTimezoneId)?.name === moment.tz.zone(timezoneId)?.name;
  return areTimezonesEquivalent;
}

export const getTimeStringFromMinutes = (minutes: number | null | undefined) => {
  if (minutes) {
    const hour = Math.floor((minutes || 0) / 60);
    const min = minutes - hour * 60;
    if (hour) {
      return `${hour} hour${hour === 1 ? "" : "s"}${min > 0 ? ` ${min} min${min === 1 ? "" : "s"}` : ""}`;
    } else if (min) {
      return `${min} min${min === 1 ? "" : "s"}`;
    } else {
      return "0";
    }
  }

  return "0";
};

// Buckets a past instant into the largest unit where the diff fits, for i18n relative-time rendering.
// Returns `{ range: "minute" | "hour" | "day" | "month" | "year", time }`,
// where `time` is the signed value to feed into the locale string (positive = in the future).
export const getRelativeTimeLocaleOption = (time: MomentInput) => {
  const timeObj = moment.utc(time);
  // For each unit, the threshold = how many of that unit before we promote to the next coarser one.
  const unitThresholds = {
    minute: 60,
    hour: 12,
    day: 30,
    month: 12,
  } satisfies Record<string, number>;

  const range =
    (Object.keys(unitThresholds) as Array<keyof typeof unitThresholds>).find(
      unit => moment.utc().diff(timeObj, `${unit}s`) < unitThresholds[unit]
    ) ?? "year";

  return { range, time: -moment.utc().diff(timeObj, `${range}s`) };
};
